<template>
  <div
    v-if="result"
    class="transaction-session-details"
  >
    <!-- 1. Retirada (Take Point) -->
    <div class="history-section">
      <div class="flex-history-element">
        <div class="flex-history-icon">
          <font-awesome-icon
            icon="fa-solid fa-arrow-right-to-bracket"
            class="fa fa-rotate-90"
          />
        </div>
        <div class="flex-history-description">
          <span
            class="flex-history-title"
            v-text="$t('transaction_info.take')"
          ></span>
          <template v-if="hasPointInfo(session.take_point)">
            <a
              :href="getMapLink(session.take_point)"
              target="_blank"
              rel="noopener noreferrer"
              class="addr-link"
            >
              {{ getShortAddress(session.take_point) }}
            </a>
          </template>
          <span
            v-else
            class="flex-history-text"
          >-</span>
          <span class="history-time">
            <font-awesome-icon
              icon="fa-regular fa-clock"
              class="time-icon"
            />
            {{ formatShortDate(session.take_time) }}
          </span>
        </div>
      </div>
    </div>

    <!-- 2. Devolução (Return Point) -->
    <div class="history-section">
      <div class="flex-history-element">
        <div class="flex-history-icon">
          <font-awesome-icon
            icon="fa-solid fa-arrow-up-from-bracket"
            class="fa"
          />
        </div>
        <div class="flex-history-description">
          <span
            class="flex-history-title"
            v-text="$t('transaction_info.return')"
          ></span>
          <template v-if="hasPointInfo(session.return_point)">
            <a
              :href="getMapLink(session.return_point)"
              target="_blank"
              rel="noopener noreferrer"
              class="addr-link"
            >
              {{ getShortAddress(session.return_point) }}
            </a>
          </template>
          <span
            v-else
            class="flex-history-text"
          >{{ returnBattery(session) }}</span>
          <span
            v-if="session.return_time"
            class="history-time"
          >
            <font-awesome-icon
              icon="fa-regular fa-clock"
              class="time-icon"
            />
            {{ formatShortDate(session.return_time) }}
          </span>
        </div>
      </div>
    </div>

    <!-- 3. Tempo de Uso (Time) -->
    <div class="history-section">
      <div class="flex-history-element">
        <div class="flex-history-icon">
          <font-awesome-icon
            icon="fa-solid fa-clock-rotate-left"
            class="fa mirror"
          />
        </div>
        <div class="flex-history-description">
          <div
            class="flex-history-title"
            v-text="$t('transaction_info.time')"
          ></div>
          <div class="flex-history-text">
            {{ rentTime(session) }}
          </div>
        </div>
      </div>
    </div>

    <!-- 4. Pagamento (Payment) -->
    <div class="history-section">
      <div class="flex-history-element">
        <div class="flex-history-icon">
          <font-awesome-icon
            icon="fa-solid fa-dollar-sign"
            class="fa"
          />
        </div>
        <div class="flex-history-description">
          <div
            v-if="session.status === 'take_battery_success' || session.status === 2"
            class="flex-history-title"
            v-text="$t('transaction_info.reserve')"
          ></div>
          <div
            v-else
            class="flex-history-title"
            v-text="$t('transaction_info.payment')"
          ></div>

          <!-- Refund Information with detailed breakdown -->
          <div
            v-if="hasRefundInfo(session)"
            class="refund-info-container"
          >
            <div class="refund-info-row">
              <span class="refund-label">{{ $t('history.deposit_held') }}:</span>
              <span class="refund-value">R$ {{ refundPrice(session).prePaymentFormatted }}</span>
            </div>
            <div class="refund-info-row">
              <span class="refund-label">{{ $t('history.amount_charged') }}:</span>
              <span class="refund-value">{{ price(session) }}</span>
            </div>
            <div class="refund-info-row refund-highlight">
              <span class="refund-label">{{ $t('history.amount_refunded') }}:</span>
              <span class="refund-value refund-amount">R$ {{ refundPrice(session).refundAmountFormatted }}</span>
            </div>
          </div>

          <!-- Regular price display -->
          <div
            v-else
            class="flex-history-text"
          >
            <span
              :class="{
                'line-through': isRefunded(session)
              }"
            >
              {{ price(session) }}
            </span>
            <span
              v-if="isRefunded(session)"
              class="refund-badge-inline"
            >
              {{ $t('transaction_info.refund') }}
            </span>
          </div>

          <div class="flex-history-text">
            {{ paymentType(session) }}
          </div>
        </div>
      </div>
    </div>

    <!-- 5. Classificação (Rating) -->
    <div
      v-if="showTransactionRating"
      class="history-section"
    >
      <div class="flex-history-element">
        <div class="flex-history-icon">
          <font-awesome-icon
            icon="fa-solid fa-star"
            class="fa"
          />
        </div>
        <div class="flex-history-description">
          <div
            class="flex-history-title"
            v-text="$t('transaction_info.rating')"
          ></div>
          <div
            v-if="session?.rating"
            class="flex-history-star"
          >
            <div
              v-for="star in 5"
              :key="star"
            >
              <font-awesome-icon
                icon="fa-solid fa-star"
                :class="[star <= session?.rating ? 'fa-minor flex-history-star-on' : 'fa-minor flex-history-star-off']"
                class="fa"
              />
            </div>
          </div>
          <div
            v-else
            class="flex-history-text"
            v-text="$t('transaction_info.no_rating')"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { formatToBrazilianReal } from '@/utils/format'
  import { isTransactionRefunded } from '@/constants/status-constants'

  const CREDIT_CARD_PAYMENT = 1
  const PIX_PAYMENT = 3
  const STATUS_UNKNOWN = 15
  const PRE_PAYMENT_REFUNDED = 6

  export default {
    name: 'TransactionSessionDetails',
    props: {
      result: {
        type: Object,
        required: true
      },
      showTransactionRating: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        tick: 0,
        timer: null
      }
    },
    computed: {
      session() {
        if (!this.result) return null

        const normalized = { ...this.result }

        if (!normalized.take_point && normalized.rent_point) {
          normalized.take_point = normalized.rent_point
        }

        if (!normalized.return_point) {
          normalized.return_point = normalized.rent_battery_point || normalized.return_battery_point
        }

        return normalized
      }
    },
    mounted() {
      if (this.session?.return_time === 0) {
        this.startTimer()
      }
    },
    beforeUnmount() {
      this.stopTimer()
    },
    methods: {
      hasRefundInfo(session) {
        const refundPriceData = this.refundPrice(session)
        return refundPriceData && refundPriceData.prePaymentFormatted && refundPriceData.refundAmountFormatted
      },
      getMapLink(point) {
        const lat = point?.geolocation?.lat || point?.lat
        const lng = point?.geolocation?.lng || point?.lng
        if (lat && lng && lat !== '0' && lng !== '0') {
          return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
        }
        // Fallback: search by address/name
        const query = [point?.name, point?.address].filter(Boolean).join(', ')
        if (query) {
          return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
        }
        return '#'
      },
      price(session) {
        if (!session) return this.$t('transaction_info.free_transaction')

        if (session.free_time_type === 'Signature') {
          return this.$t('transaction_info.free_signature')
        }

        const normalizeStatus = s => {
          if (s == null) return ''
          return typeof s === 'string' ? s.toLowerCase() : String(s)
        }

        const payStatus = normalizeStatus(session.payment?.status)
        const isTransactionCaptured = session.payment && ['approved', 'captured', 'partial', '3', '4'].includes(payStatus)
        const hasPaymentAmount = session.payment?.amount !== undefined && session.payment?.amount !== null

        // 1. Se tem pagamento final capturado/aprovado, esse é SEMPRE o valor oficial da locação
        if (isTransactionCaptured && hasPaymentAmount) {
          if (parseFloat(session.payment.amount) === 0) return this.$t('transaction_info.free_transaction')
          return formatToBrazilianReal(session.payment.amount)
        }

        const prePaymentStatus = normalizeStatus(session.last_pre_payment?.status)
        const hasPrePaymentAmount = session.last_pre_payment?.amount !== undefined && session.last_pre_payment?.amount !== null

        // 2. Pré-pagamento autorizado pendente (caução retida em andamento)
        const isPrePaymentAuthorized = ['authorized', '11'].includes(prePaymentStatus)
        if (isPrePaymentAuthorized && hasPrePaymentAmount) {
          if (parseFloat(session.last_pre_payment.amount) === 0) return this.$t('transaction_info.free_transaction')
          return formatToBrazilianReal(session.last_pre_payment.amount) + ' (caução)'
        }

        // 3. Pré-pagamento capturado ou estornado (sem pagamento final separado)
        const isPrePaymentFinalized = ['approved', 'captured', 'refunded', '2', '12', '6'].includes(prePaymentStatus)
        if (isPrePaymentFinalized && hasPrePaymentAmount) {
          if (parseFloat(session.last_pre_payment.amount) === 0) return this.$t('transaction_info.free_transaction')
          return formatToBrazilianReal(session.last_pre_payment.amount)
        }

        // 4. Locação em andamento ou fallback de preço de tabela
        const currentPrice = session.current_price ?? session.price
        if (currentPrice !== null && currentPrice !== undefined && Number(currentPrice) !== 0) {
          return formatToBrazilianReal(currentPrice)
        }

        return this.$t('transaction_info.free_transaction')
      },

      refundPrice(session) {
        const isPaymentApproved = ['approved', 'captured', '3', '4'].includes(
          String(session?.payment?.status ?? '').toLowerCase()
        )
        const paymentAmount = session?.payment?.amount
        const prePaymentAmount = session?.last_pre_payment?.amount
        const isPartialRefund =
          paymentAmount !== undefined &&
          paymentAmount !== null &&
          prePaymentAmount !== undefined &&
          prePaymentAmount !== null &&
          parseFloat(prePaymentAmount) > parseFloat(paymentAmount)
        if (isPaymentApproved && isPartialRefund) {
          const prePaymentFormatted = parseFloat(prePaymentAmount).toFixed(2).replace('.', ',')
          const refundAmountFormatted = parseFloat(prePaymentAmount - paymentAmount)
            .toFixed(2)
            .replace('.', ',')
          return { prePaymentFormatted, refundAmountFormatted }
        }
        return false
      },
      isRefunded(session) {
        return isTransactionRefunded(session)
      },
      paymentType(session) {
        const type = session.payment?.type ?? session.last_pre_payment?.type ?? session.payment_method
        const isCreditCard = type === 'credit_card' || type === CREDIT_CARD_PAYMENT
        const isPix = type === 'pix' || type === PIX_PAYMENT

        if (isCreditCard) {
          if (session.credit_card?.number) {
            return this.$t('transaction_info.credit_card', { number: session.credit_card.number })
          }
          return this.$t('select_payment_method.card_title')
        }
        if (isPix) {
          return this.$t('transaction_info.pix')
        }

        if (session.payment_method) {
          return session.payment_method.charAt(0).toUpperCase() + session.payment_method.slice(1)
        }
        return '-'
      },
      startTimer() {
        this.stopTimer()
        this.timer = setInterval(() => {
          this.tick++
        }, 30000)
      },
      stopTimer() {
        if (this.timer) {
          clearInterval(this.timer)
          this.timer = null
        }
      },
      rentTime(session) {
        if (session?.cron_time) {
          return session?.cron_time
        }
        if (session?.rent_time?.all) {
          return session?.rent_time?.all
        }

        // For active rentals, calculate from take_time to now
        if (session.return_time === 0 && session.take_time) {
          const nowSeconds = Math.floor(Date.now() / 1000)
          const totalSeconds = nowSeconds - session.take_time
          return this.formatDuration(totalSeconds)
        }

        const totalSeconds = session?.elapsed_time
        if (totalSeconds === undefined || totalSeconds === null) return '-'
        if (totalSeconds === 0) return '0 segundo'

        return this.formatDuration(totalSeconds)
      },
      formatDuration(totalSeconds) {
        const days = Math.floor(totalSeconds / 86400)
        const hours = Math.floor((totalSeconds % 86400) / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60

        const parts = []
        if (days > 0) parts.push(`${days} ${days === 1 ? 'dia' : 'dias'}`)
        if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hora' : 'horas'}`)
        if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`)
        if (seconds > 0) parts.push(`${seconds} ${seconds === 1 ? 'segundo' : 'segundos'}`)

        return parts.join(', ') || '0 segundos'
      },
      returnBattery(session) {
        if (this.hasPointInfo(session.return_point)) {
          return this.getPointName(session.return_point)
        }

        if (session.return_time === 0) {
          return this.$t('history.status_pending')
        }

        if (session.status === 'battery_lost' || session.status === 'unknown' || session.status === STATUS_UNKNOWN) {
          return '-'
        }
        return this.$t('transaction_info.with_customer')
      },
      hasPointInfo(point) {
        return !!(point?.name && point?.address)
      },
      getShortAddress(point) {
        if (!point) return '-'
        if (!point.address) return point.name || '-'
        const parts = point.address.split(',').map(s => s.trim())
        const short = parts.slice(0, 2).join(', ')
        if (point.name && short) {
          return `${point.name}, ${short}`
        }
        return point.name || short || '-'
      },
      formatShortDate(timestamp) {
        if (!timestamp || timestamp === 0) return ''
        const date = new Date(timestamp * 1000)
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${day}/${month} ${hours}h${minutes}`
      },
      getPointName(point) {
        if (!point) return '-'
        return `${point.name}, ${point.address}`
      }
    }
  }
</script>

<style scoped lang="scss">
  @use '@/main';

  .fa-rotate-90 {
    transform: rotate(90deg);
  }

  .history-section {
    margin: 6px 0;
  }

  .flex-history-element {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: 8px;
  }

  .flex-history-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 24px;
    max-width: 24px;

    .fa {
      font-size: 16px;
      color: main.$color-energy-orange;
    }
  }

  .flex-history-description {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 3px 6px;
    color: #111827;
  }

  .flex-history-title {
    margin: 0 !important;
    padding: 0 !important;
    font-size: 13px;
    font-weight: 600;
    color: #111827;
  }

  .flex-history-text {
    color: #6b7280;
    font-size: 13px;
    line-height: 1.4;
    margin: 0 !important;
    padding: 0 !important;
  }

  .flex-history-star {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    gap: 3px;
  }

  .flex-history-star-on {
    color: #f59e0b;
  }

  .flex-history-star-off {
    color: #d1d5db;
  }

  .mirror {
    transform: scaleX(-1);
  }

  .fa-minor {
    font-size: 18px;
  }

  .line-through {
    text-decoration: line-through;
  }

  .addr-link {
    font-size: 12px;
    color: #9ca3af;
    text-decoration: underline;
    text-underline-offset: 2px;
    text-decoration-thickness: 1px;
    text-decoration-color: #d1d5db;
    transition: color 0.15s;

    &:hover {
      color: #4b5563;
      text-decoration-color: #4b5563;
    }
  }

  .history-time {
    font-size: 12px;
    color: #9ca3af;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }

  .time-icon {
    font-size: 11px;
    color: #9ca3af;
  }

  .refund-info-container {
    width: 100%;
  }

  .refund-info-row {
    display: flex;
    justify-content: space-between;
    font-size: 13px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .refund-label {
    color: #6b7280;
    font-size: 13px;
  }

  .refund-value {
    font-weight: 500;
    color: #374151;
    font-size: 13px;
  }

  .refund-highlight {
    margin-top: 2px;
    padding-top: 2px;
  }

  .refund-amount {
    color: #059669;
    font-weight: 700;
  }

  .refund-badge-inline {
    background: #def7ec;
    color: #03543f;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 4px;
    margin-left: 6px;
    text-transform: uppercase;
  }
</style>
